"use strict";

const swiftrefInput = require("src/components/form/input");
const baseTemplate = document.createElement("template");
baseTemplate.innerHTML = require("./template");
const typography = require("!!css-loader!../../../tokens/typography.css").default;

class VanillaTimePicker extends swiftrefInput {
    constructor() {
        super(baseTemplate);

        /**
         * Applying styles inside the shadow DOM:
         * @see https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_shadow_DOM#applying_styles_inside_the_shadow_dom
         */
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(typography);
        this.root.adoptedStyleSheets = [sheet];

        // State
        this._isOpen = false;
        this._selectedTime = null; // Will store Date object for the time value
        this._use24HourFormat = false;
        this._includeSeconds = true;
        this._mouseDown = false;
        this._timeFormat = 'hh:mm:ss';
        this._timeStep = 1; // Default step for seconds and minutes
        this._minuteStep = 1;
        this._hourStep = 1;

        // DOM elements references
        this._elements = {
            input: null,
            timePanel: null,
            hourSelector: null,
            minuteSelector: null,
            secondSelector: null,
            amPmSelector: null
        };

        this._elements.input = this.root.querySelector(".swiftref-input input");
        this._elements.timePanel = this.shadowRoot.querySelector('.timepicker-panel');
        this._elements.hourSelector = this.shadowRoot.querySelector('.hour-selector');
        this._elements.minuteSelector = this.shadowRoot.querySelector('.minute-selector');
        this._elements.secondSelector = this.shadowRoot.querySelector('.second-selector');
        this._elements.amPmSelector = this.shadowRoot.querySelector('.ampm-selector');
        this._elements.footer = this.shadowRoot.querySelector('.panel-footer');

        this._handleInputClick = this._handleInputClick.bind(this);
        this._handleWheelScroll = this._handleWheelScroll.bind(this);
        this._handleKeyDown = this._handleKeyDown.bind(this);
        this._handleAmPmToggle = this._handleAmPmToggle.bind(this);
        this._handleOutsideClick = this._handleOutsideClick.bind(this);
    }

    // Lifecycle methods
    connectedCallback() {
        super.connectedCallback();
        this._setupEventListeners();

        // Initialize with any provided attributes
        this._initializeFromAttributes();

        // Update the display
        this._updateDisplay();

        // Fire ready event
        this.dispatchEvent(new CustomEvent('timepicker-ready'));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._removeEventListeners();
    }

    static get observedAttributes() {
        return super.observedAttributes.concat([
            'time-format', 'use-24-hour-format', 'include-seconds', 'time-step', 'minute-step', 'hour-step', 'headless'
        ]);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        if (oldValue === newValue) return;

        switch (name) {
            case 'time-format':
                this._timeFormat = newValue || 'hh:mm:ss';
                this._updateInputValue();
                break;
            case 'use-24-hour-format':
                this._use24HourFormat = this.hasAttribute('use-24-hour-format');
                this._updateDisplay();
                break;
            case 'include-seconds':
                this._includeSeconds = this.hasAttribute('include-seconds');
                this._updateDisplay();
                break;
            case 'value':
                if (newValue) {
                    // Try to parse the time from string format "HH:MM:SS" or "HH:MM"
                    const parts = newValue.split(':');
                    if (parts.length >= 2) {
                        const now = new Date();
                        now.setUTCHours(parseInt(parts[0] || 0, 10));
                        now.setUTCMinutes(parseInt(parts[1] || 0, 10));
                        now.setUTCSeconds(parts.length > 2 ? parseInt(parts[2], 10) : 0);
                        now.setUTCMilliseconds(0);
                        this._selectedTime = now;
                        this._updateInputValue();
                        this._updateSelectors();
                    }
                }
                break;
            case 'time-step':
                this._timeStep = parseInt(newValue, 10) || 1;
                break;
            case 'minute-step':
                this._minuteStep = parseInt(newValue, 10) || 1;
                this._updateSelectors();
                break;
            case 'hour-step':
                this._hourStep = parseInt(newValue, 10) || 1;
                this._updateSelectors();
                break;
            case 'headless':
                if (this.hasAttribute("headless")) {
                    this.open();
                }
                break;
            default:
                break;
        }
    }

    // Public API
    get value() {
        return this._elements.input ? this._elements.input.value : '';
    }

    set value(val) {
        if (!val) {
            this.clear();
            return;
        }

        const parts = val.split(':');
        if (parts.length >= 2) {
            if (!this._selectedTime) {
                this._selectedTime = new Date();
            }

            this._selectedTime.setUTCHours(parseInt(parts[0] || 0, 10));
            this._selectedTime.setUTCMinutes(parseInt(parts[1] || 0, 10));
            this._selectedTime.setUTCSeconds(parts.length > 2 ? parseInt(parts[2], 10) : 0);
            this._selectedTime.setUTCMilliseconds(0);

            this._updateInputValue();
            this._updateSelectors();
        }
    }

    get selectedTime() {
        return this._selectedTime ? new Date(this._selectedTime) : null;
    }

    open() {
        if (this._isOpen) return;

        this._isOpen = true;
        this._elements.timePanel.classList.add('open');

        // Set the selectors to the current selected time
        this._updateSelectors();

        // Fire open event
        this.dispatchEvent(new CustomEvent('timepicker-opened'));
    }

    close() {
        if (!this._isOpen) return;

        this._isOpen = false;
        this._elements.timePanel.classList.remove('open');

        // Fire close event
        this.dispatchEvent(new CustomEvent('timepicker-closed'));
    }

    clear() {
        this._selectedTime = new Date();
        this._selectedTime.setUTCHours(0, 0, 0, 0);
        this._updateInputValue();

        this._elements.input.value = "";
        // Fire change event
        this._fireChangeEvent();
    }

    _setupEventListeners() {
        this._elements.input.addEventListener('click', this._handleInputClick);

        // Hour selector events
        this._elements.hourSelector.addEventListener('wheel', this._handleWheelScroll);
        this._elements.hourSelector.addEventListener('keydown', this._handleKeyDown);

        // Minute selector events
        this._elements.minuteSelector.addEventListener('wheel', this._handleWheelScroll);
        this._elements.minuteSelector.addEventListener('keydown', this._handleKeyDown);

        // Second selector events
        this._elements.secondSelector.addEventListener('wheel', this._handleWheelScroll);
        this._elements.secondSelector.addEventListener('keydown', this._handleKeyDown);

        // AM/PM selector events
        if (this._elements.amPmSelector) {
            this._elements.amPmSelector.addEventListener('click', this._handleAmPmToggle);
            this._elements.amPmSelector.addEventListener('keydown', e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    this._handleAmPmToggle();
                    e.preventDefault();
                }
            });
        }

        // Increment/decrement buttons
        const incrementBtns = this.shadowRoot.querySelectorAll('.increment-btn');
        const decrementBtns = this.shadowRoot.querySelectorAll('.decrement-btn');

        incrementBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const type = btn.dataset.type;
                this._incrementTime(type);
            });
        });

        decrementBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const type = btn.dataset.type;
                this._decrementTime(type);
            });
        });

        // Footer buttons
        const nowBtn = this.shadowRoot.querySelector('.now-btn');
        const clearBtn = this.shadowRoot.querySelector('.clear-btn');
        const doneBtn = this.shadowRoot.querySelector('.done-btn');
        //const closeBtn = this.shadowRoot.querySelector('.close-btn');

        nowBtn.addEventListener('click', () => {
            this._selectedTime = new Date();
            this._updateInputValue();
            this._updateSelectors();
            this._fireChangeEvent();
        });

        clearBtn.addEventListener('click', () => {
            this.clear();
            this._updateSelectors();
        });

        doneBtn.addEventListener('click', () => {
            this.close();
        });

        // Attach global event for closing when clicking outside
        document.addEventListener('mousedown', this._handleOutsideClick);
    }

    _removeEventListeners() {
        document.removeEventListener('mousedown', this._handleOutsideClick);
    }

    _handleInputClick() {
        if (this._isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    _handleOutsideClick(e) {
        const path = e.composedPath ? e.composedPath() : e.path || [];
        if (!path.includes(this) && this._isOpen) {
            this.close();
        }
    }

    _handleWheelScroll(e) {
        e.preventDefault();

        const target = e.currentTarget;
        const direction = e.deltaY < 0 ? 1 : -1;

        if (target === this._elements.hourSelector) {
            this._incrementTime('hour', direction);
        } else if (target === this._elements.minuteSelector) {
            this._incrementTime('minute', direction);
        } else if (target === this._elements.secondSelector) {
            this._incrementTime('second', direction);
        }
    }

    _handleKeyDown(e) {
        const target = e.currentTarget;

        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                if (target === this._elements.hourSelector) {
                    this._incrementTime('hour', 1);
                } else if (target === this._elements.minuteSelector) {
                    this._incrementTime('minute', 1);
                } else if (target === this._elements.secondSelector) {
                    this._incrementTime('second', 1);
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (target === this._elements.hourSelector) {
                    this._incrementTime('hour', -1);
                } else if (target === this._elements.minuteSelector) {
                    this._incrementTime('minute', -1);
                } else if (target === this._elements.secondSelector) {
                    this._incrementTime('second', -1);
                }
                break;
            case 'Tab':
                // Handle tabbing between selectors
                if (e.shiftKey && target === this._elements.hourSelector) {
                    // Loop back to close button when shift+tab on the first element
                    //this.shadowRoot.querySelector('.close-btn').focus();
                    e.preventDefault();
                } else if (!e.shiftKey && target === this._elements.secondSelector && !this._elements.amPmSelector.style.display) {
                    // If AM/PM selector is not visible and we're on the last selector (seconds)
                    this.shadowRoot.querySelector('.now-btn').focus();
                    e.preventDefault();
                } else if (!e.shiftKey && target === this._elements.amPmSelector) {
                    // If we're on AM/PM and tabbing forward
                    this.shadowRoot.querySelector('.now-btn').focus();
                    e.preventDefault();
                }
                break;
            case 'Escape':
                this.close();
                break;
        }
    }

    _handleAmPmToggle() {
        if (!this._selectedTime) return;

        const hours = this._selectedTime.getUTCHours();
        if (hours >= 12) {
            // Switch to AM
            this._selectedTime.setUTCHours(hours - 12);
            this._elements.amPmSelector.textContent = 'AM';
        } else {
            // Switch to PM
            this._selectedTime.setUTCHours(hours + 12);
            this._elements.amPmSelector.textContent = 'PM';
        }

        this._updateInputValue();
        this._updateHourSelector();
        this._fireChangeEvent();
    }

    _incrementTime(type, direction = 1) {
        if (!this._selectedTime) {
            this._selectedTime = new Date();
        }

        const multiplier = direction > 0 ? 1 : -1;

        switch (type) {
            case 'hour': {
                const hourStep = this._hourStep * multiplier;
                const currentHours = this._selectedTime.getUTCHours();
                let newHours;

                if (this._use24HourFormat) {
                    newHours = (currentHours + hourStep + 24) % 24;
                } else {
                    // For 12-hour format, need to handle AM/PM
                    const isAM = currentHours < 12;
                    const display12Hour = currentHours % 12 === 0 ? 12 : currentHours % 12;
                    let new12Hour = (display12Hour + hourStep) % 12;
                    if (new12Hour <= 0) new12Hour = 12;

                    newHours = isAM ? new12Hour % 12 : (new12Hour % 12) + 12;

                    // Update AM/PM display if we cross the threshold
                    if ((currentHours < 12 && newHours >= 12) || (currentHours >= 12 && newHours < 12)) {
                        this._elements.amPmSelector.textContent = newHours >= 12 ? 'PM' : 'AM';
                    }
                }

                this._selectedTime.setUTCHours(newHours);
                break;
            }

            case 'minute': {
                const minuteStep = this._minuteStep * multiplier;
                const currentMinutes = this._selectedTime.getUTCMinutes();
                const newMinutes = (currentMinutes + minuteStep + 60) % 60;

                // If we wrap around, increment/decrement the hour
                if ((currentMinutes + minuteStep < 0) || (currentMinutes + minuteStep >= 60)) {
                    this._incrementTime('hour', minuteStep < 0 ? -1 : 1);
                }

                this._selectedTime.setUTCMinutes(newMinutes);
                break;
            }
            case 'second': {
                const secondStep = this._timeStep * multiplier;
                const currentSeconds = this._selectedTime.getUTCSeconds();
                const newSeconds = (currentSeconds + secondStep + 60) % 60;

                // If we wrap around, increment/decrement the minute
                if ((currentSeconds + secondStep < 0) || (currentSeconds + secondStep >= 60)) {
                    this._incrementTime('minute', secondStep < 0 ? -1 : 1);
                }

                this._selectedTime.setUTCSeconds(newSeconds);
                break;
            }
        }

        this._updateInputValue();
        this._updateSelectors();
        this._fireChangeEvent();
    }

    _decrementTime(type) {
        this._incrementTime(type, -1);
    }

    _handleTimeSelection(e) {
        // This could be used for direct input or clicking on a time in a list
        // Currently not used, but kept for future enhancements
    }

    _initializeFromAttributes() {
        this._timeFormat = this.getAttribute('time-format') || 'hh:mm:ss';
        this._use24HourFormat = this.hasAttribute('use-24-hour-format');
        this._includeSeconds = this.hasAttribute('include-seconds');
        this._timeStep = parseInt(this.getAttribute('time-step') || '1', 10);
        this._minuteStep = parseInt(this.getAttribute('minute-step') || '1', 10);
        this._hourStep = parseInt(this.getAttribute('hour-step') || '1', 10);

        // Handle default time if provided
        // const defaultTime = this.getAttribute('default-time');
        // if (defaultTime) {
        //     const parts = defaultTime.split(':');
        //     if (parts.length >= 2) {
        //         const now = new Date();
        //         now.setUTCHours(parseInt(parts[0] || 0, 10));
        //         now.setUTCMinutes(parseInt(parts[1] || 0, 10));
        //         now.setUTCSeconds(parts.length > 2 ? parseInt(parts[2], 10) : 0);
        //         now.setUTCMilliseconds(0);
        //         this._selectedTime = now;
        //     }
        // }

        // Update display
        this._updateDisplay();
    }

    _updateDisplay() {
        // Show/hide seconds column based on the includeSeconds setting
        const secondColumn = this.shadowRoot.querySelector('.second-column');
        const secondSeparator = this.shadowRoot.querySelector('.second-separator');

        if (this._includeSeconds) {
            secondColumn.style.display = 'flex';
            secondSeparator.style.display = 'block';
        } else {
            secondColumn.style.display = 'none';
            secondSeparator.style.display = 'none';
        }

        // Show/hide AM/PM selector based on 24 hour format setting
        const ampmColumn = this.shadowRoot.querySelector('.ampm-column');

        if (this._use24HourFormat) {
            ampmColumn.style.display = 'none';
        } else {
            ampmColumn.style.display = 'flex';
        }

        // Update selectors
        this._updateSelectors();

        // Update input value
        this._updateInputValue();
    }

    _updateSelectors() {
        this._updateHourSelector();
        this._updateMinuteSelector();
        this._updateSecondSelector();
        this._updateAmPmSelector();
    }

    _updateHourSelector() {
        if (!this._selectedTime) return;

        const hours = this._selectedTime.getUTCHours();

        if (this._use24HourFormat) {
            this._elements.hourSelector.textContent = hours.toString().padStart(2, '0');
        } else {
            // Convert to 12-hour format
            const hours12 = hours % 12 === 0 ? 12 : hours % 12;
            this._elements.hourSelector.textContent = hours12.toString().padStart(2, '0');
        }
    }

    _updateMinuteSelector() {
        if (!this._selectedTime) return;

        const minutes = this._selectedTime.getUTCMinutes();
        this._elements.minuteSelector.textContent = minutes.toString().padStart(2, '0');
    }

    _updateSecondSelector() {
        if (!this._selectedTime) return;

        const seconds = this._selectedTime.getUTCSeconds();
        this._elements.secondSelector.textContent = seconds.toString().padStart(2, '0');
    }

    _updateAmPmSelector() {
        if (!this._selectedTime || this._use24HourFormat) return;

        const hours = this._selectedTime.getUTCHours();
        this._elements.amPmSelector.textContent = hours >= 12 ? 'PM' : 'AM';
    }

    _updateInputValue() {
        if (!this._elements.input || !this._selectedTime) return;

        const hours = this._selectedTime.getUTCHours();
        const minutes = this._selectedTime.getUTCMinutes();
        const seconds = this._selectedTime.getUTCSeconds();

        let formattedTime;

        if (this._use24HourFormat) {
            if (this._includeSeconds) {
                formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            } else {
                formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            }
        } else {
            const hours12 = hours % 12 === 0 ? 12 : hours % 12;
            const ampm = hours >= 12 ? 'PM' : 'AM';

            if (this._includeSeconds) {
                formattedTime = `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${ampm}`;
            } else {
                formattedTime = `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
            }
        }

        this._elements.input.value = formattedTime;
    }

    _fireChangeEvent() {
        const time = this._selectedTime ? {
            hours: this._selectedTime.getUTCHours(),
            minutes: this._selectedTime.getUTCMinutes(),
            seconds: this._selectedTime.getUTCSeconds()
        } : null;

        const event = new CustomEvent('change', {
            detail: {
                time: time,
                timeStr: this._elements.input.value
            },
            bubbles: true,
            composed: true
        });

        this.dispatchEvent(event);
    }
}

// Define the custom element
customElements.define('swiftref-time-input', VanillaTimePicker);
